import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Grid,
  Paper,
  Chip,
  FormHelperText,
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
  Divider,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as InspectorIcon,
  Visibility as ViewerIcon,
  Business as ManagerIcon,
  Password as PasswordIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  AutoAwesome as GenerateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import PermissionScopeSelector from './PermissionScopeSelector';
import NetworkSectionSelector from './NetworkSectionSelector';
import { disciplinesApi } from '../../services/api';

const EditUserModal = ({ open, onClose, user, onUserUpdated }) => {
  const { user: currentUser, refreshUser } = useAuth();
  const isManagerEditingSelf = currentUser?.role === 'manager' &&
    user?.role === 'manager' && user?.id === currentUser?.id;
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [assignableResources, setAssignableResources] = useState(null);
  const [availableDisciplines, setAvailableDisciplines] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    role: '',
    permissionScope: '',
    permissions: {
      locations: [],
      disciplines: [],
      sub_disciplines: []
    },
    isActive: true
  });

  // Password management states
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    showPassword: false,
    isGenerating: false
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Track what fields have changed
  const [hasChanges, setHasChanges] = useState(false);
  const [hasPasswordChanges, setHasPasswordChanges] = useState(false);

  // Success states
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Initialize form data when user prop changes
  useEffect(() => {
    if (user && open) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        fullName: user.full_name || '',
        role: user.role || '',
        permissionScope: user.permission_scope || 'organization',
        permissions: {
          locations: [],
          disciplines: user.disciplines || [],
          sub_disciplines: user.sub_disciplines || []
        },
        isActive: user.is_active !== false
      });
      setError(null);
      setErrors({});
      setHasChanges(false);
      setHasPasswordChanges(false);
      setPasswordSuccess(false);
      setPermissionsLoaded(false);
      fetchAssignableResources();
      fetchAvailableDisciplines();
      // Fetch complete user permissions including locations
      fetchUserPermissions();
    }
  }, [user, open]);

  const fetchAvailableDisciplines = async () => {
    try {
      const response = await disciplinesApi.getDisciplines();
      setAvailableDisciplines(response.data || []);
    } catch (error) {
      console.error('Error fetching disciplines:', error);
      setError('Failed to load available disciplines.');
    }
  };

  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const response = await authService.getUserPermissions(user.id);
      if (response.success && response.permissions) {
        // Transform location permissions to the format expected by NetworkSectionSelector
        const transformedLocations = (response.permissions.locations || []).map(permission => {
          if (permission.network_id && !permission.section_id) {
            // Network permission
            return {
              id: permission.network_id,
              type: 'network',
              name: permission.network_name || `Network ${permission.network_id}`,
              description: permission.description || ''
            };
          } else if (permission.section_id) {
            // Section permission - use section_network_id from the backend
            return {
              id: permission.section_id,
              type: 'section', 
              name: permission.section_name || `Section ${permission.section_id}`,
              description: `${permission.section_network_name} Network` || '',
              networkId: permission.section_network_id || null
            };
          }
          return null;
        }).filter(Boolean);

        // Update formData with the complete permissions
        setFormData(prev => ({
          ...prev,
          permissions: {
            locations: transformedLocations,
            disciplines: response.permissions.disciplines || [],
            sub_disciplines: response.permissions.sub_disciplines || []
          }
        }));
        setPermissionsLoaded(true);
      } else {
        setError('Failed to load user permissions. Close and reopen this dialog to try again.');
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setError('Failed to load user permissions. Close and reopen this dialog to try again.');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const fetchAssignableResources = async () => {
    try {
      const response = await authService.getAssignableResources();
      if (response.success) {
        setAssignableResources(response.resources);
      }
    } catch (error) {
      console.error('Error fetching assignable resources:', error);
    }
  };


  const validateForm = () => {
    const newErrors = {};

    if (!isManagerEditingSelf) {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.permissionScope) {
      newErrors.permissionScope = 'Permission scope is required';
    }

    if (formData.permissionScope === 'network_section' && formData.permissions.locations.length === 0) {
      newErrors.permissions = 'Please select at least one network or section for network/section level access';
    }

    if (formData.permissions.disciplines.length === 0) {
      newErrors.disciplines = 'Please select at least one discipline for the user.';
    }

    if (isManagerEditingSelf &&
        formData.permissions.disciplines.some(d => d.name === 'Road Pavement') &&
        formData.permissions.sub_disciplines.length === 0) {
      newErrors.disciplines = 'Please select at least one Road Pavement sub-discipline.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    setHasChanges(true);

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Reset permissions if changing permission scope
    if (field === 'permissionScope') {
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          locations: []
        }
      }));
    }
  };

  const handlePermissionsChange = (locations) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        locations
      }
    }));
    setHasChanges(true);

    if (errors.permissions) {
      setErrors(prev => ({
        ...prev,
        permissions: null
      }));
    }
  };

  const handleDisciplineChange = (disciplineId, isChecked) => {
    let newDisciplines = [...formData.permissions.disciplines];
    let newSubDisciplines = [...formData.permissions.sub_disciplines];
    
    if (isChecked) {
      const discipline = availableDisciplines.find(d => d.id === disciplineId);
      if (discipline) {
        newDisciplines.push({ id: discipline.id, name: discipline.name });
      }
    } else {
      newDisciplines = newDisciplines.filter(d => d.id !== disciplineId);
      
      // Find the discipline and remove all its sub-disciplines when unchecked
      const discipline = availableDisciplines.find(d => d.id === disciplineId);
      if (discipline && discipline.sub_disciplines && discipline.sub_disciplines.length > 0) {
        // Remove all sub-disciplines of this discipline
        const subDisciplineNames = discipline.sub_disciplines.map(sd => sd.name);
        newSubDisciplines = newSubDisciplines.filter(sd => !subDisciplineNames.includes(sd));
      }
    }
    
    setFormData(prev => ({
      ...prev,
      permissions: { 
        ...prev.permissions, 
        disciplines: newDisciplines,
        sub_disciplines: newSubDisciplines
      }
    }));
    setHasChanges(true);
  };

  const handleSubDisciplineChange = (subDisciplineName, isChecked) => {
    if (isChecked) {
      // Check if the parent discipline is selected
      const parentDiscipline = availableDisciplines.find(d => 
        d.sub_disciplines && d.sub_disciplines.some(sd => sd.name === subDisciplineName)
      );
      
      if (parentDiscipline) {
        const isParentSelected = formData.permissions.disciplines.some(d => d.id === parentDiscipline.id);
        if (!isParentSelected) {
          // Show error or prevent selection - for now, we'll silently prevent it
          // You could show a toast/alert here: "Please select the parent discipline first"
          return;
        }
      }
    }
    
    let newSubDisciplines = [...formData.permissions.sub_disciplines];
    if (isChecked) {
      if (!newSubDisciplines.includes(subDisciplineName)) {
        newSubDisciplines.push(subDisciplineName);
      }
    } else {
      newSubDisciplines = newSubDisciplines.filter(sd => sd !== subDisciplineName);
    }
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, sub_disciplines: newSubDisciplines }
    }));
    setHasChanges(true);
  };

  // Password management functions
  const generatePassword = () => {
    setPasswordData(prev => ({ ...prev, isGenerating: true }));
    
    // Generate a secure password
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one character from each category
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special
    
    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setTimeout(() => {
      setPasswordData(prev => ({ 
        ...prev, 
        newPassword: password, 
        isGenerating: false, 
        showPassword: true 
      }));
      setHasPasswordChanges(true);
      setPasswordSuccess(false);
    }, 500);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  const handlePasswordChange = (value) => {
    setPasswordData(prev => ({ ...prev, newPassword: value }));
    setHasPasswordChanges(!!value);
    setPasswordSuccess(false);
  };

  const handleResetPassword = async () => {
    if (!passwordData.newPassword.trim()) {
      setError('Please enter a new password or generate one');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await authService.resetUserPassword({
        userId: user.id,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        setPasswordSuccess(true);
        setHasPasswordChanges(false);
        setPasswordData(prev => ({ ...prev, newPassword: '', showPassword: false }));
      } else {
        const errMsg = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || 'Failed to reset password';
        setError(errMsg);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEditPermissions() || !permissionsLoaded) return;
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Prepare update data with correct field names for backend
      const updateData = {
        userId: user.id,
        ...(canEditRole() ? {
          username: formData.username,
          email: formData.email,
          full_name: formData.fullName,
          role: formData.role
        } : {}),
        permission_scope: formData.permissionScope,  // Backend expects permission_scope
        permissions: {
          locations: formData.permissionScope === 'network_section' ? formData.permissions.locations : [],
          disciplines: formData.permissions.disciplines,
          sub_disciplines: formData.permissions.sub_disciplines
        }
      };

      const response = await authService.updateUser(updateData);

      if (response.success) {
        if (user.id === currentUser?.id) {
          await refreshUser();
        }
        onUserUpdated(response.user);
        onClose();
      } else {
        setError(response.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminIcon />;
      case 'manager': return <ManagerIcon />;
      case 'inspector': return <InspectorIcon />;
      case 'viewer': return <ViewerIcon />;
      default: return <PersonIcon />;
    }
  };

  const canEditRole = () => {
    // System admins can edit any role except changing other admins
    if (currentUser?.role === 'admin' && currentUser?.organizationId === null) {
      return user?.role !== 'admin' || user?.id === currentUser?.id;
    }
    
    // Managers can only edit inspector and viewer roles
    if (currentUser?.role === 'manager') {
      return ['inspector', 'viewer'].includes(user?.role);
    }
    
    return false;
  };

  const canEditPermissions = () => canEditRole() || isManagerEditingSelf;

  const getAvailableRoles = () => {
    if (currentUser?.role === 'admin' && currentUser?.organizationId === null) {
      // System admin can assign any role except admin to others
      if (user?.id === currentUser?.id) {
        return [{ value: 'admin', label: 'System Admin' }];
      }
      return [
        { value: 'manager', label: 'Manager' },
        { value: 'inspector', label: 'Inspector' },
        { value: 'viewer', label: 'Viewer' }
      ];
    }
    
    if (currentUser?.role === 'manager') {
      if (user?.role === 'manager') {
        return [{ value: 'manager', label: 'Manager' }];
      }
      return [
        { value: 'inspector', label: 'Inspector' },
        { value: 'viewer', label: 'Viewer' }
      ];
    }
    
    return [];
  };

  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            {getRoleIcon(user.role)}
            <Typography variant="h6" fontWeight={600}>Edit User</Typography>
          </Stack>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading || permissionsLoading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Updating user...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* User Information */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <PersonIcon />
                <Typography variant="h6" fontWeight={600}>User Information</Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    error={!!errors.username}
                    helperText={errors.username}
                    disabled={!canEditRole()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={!canEditRole()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    disabled={!canEditRole()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        disabled={user?.id === currentUser?.id}
                      />
                    }
                    label="Active User"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Role & Permissions */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <SecurityIcon />
                <Typography variant="h6" fontWeight={600}>Role & Permissions</Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel id="edit-user-role-label">User Role</InputLabel>
                    <Select
                      labelId="edit-user-role-label"
                      value={formData.role}
                      label="User Role"
                      onChange={(e) => handleChange('role', e.target.value)}
                      disabled={!canEditRole()}
                    >
                      {getAvailableRoles().map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {getRoleIcon(role.value)}
                            <Typography>{role.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      Current Permission Scope:
                    </Typography>
                    <Chip
                      label={
                        user.permission_scope === 'organization' 
                          ? 'Organization-Level' 
                          : 'Network/Section-Level'
                      }
                      color={user.permission_scope === 'organization' ? 'success' : 'secondary'}
                      size="small"
                    />
                  </Stack>
                </Grid>
              </Grid>

              {canEditPermissions() && (isManagerEditingSelf || ['inspector', 'viewer'].includes(formData.role)) && (
                <Box sx={{ mt: 3 }}>
                  <PermissionScopeSelector
                    value={formData.permissionScope}
                    onChange={(value) => handleChange('permissionScope', value)}
                    error={errors.permissionScope}
                    role={formData.role}
                  />
                </Box>
              )}
            </Paper>

            {/* Network/Section Assignment */}
            {canEditPermissions() && formData.permissionScope === 'network_section' && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <AssignmentIcon />
                  <Typography variant="h6" fontWeight={600}>Network & Section Assignment</Typography>
                </Stack>
                
                <NetworkSectionSelector
                  assignableResources={assignableResources}
                  selectedPermissions={formData.permissions.locations}
                  onChange={handlePermissionsChange}
                  error={errors.permissions}
                />
              </Paper>
            )}

            {/* Discipline Management */}
            {canEditPermissions() && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <AssignmentIcon />
                  <Typography variant="h6" fontWeight={600}>Discipline Management</Typography>
                </Stack>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Discipline Access:</strong> Grants access to specific asset categories and workflows.
                    <br />
                    <strong>Sub-discipline Access:</strong> Provides granular access to specific sub-categories within a discipline.
                  </Typography>
                </Alert>

                <FormControl component="fieldset" error={!!errors.disciplines} sx={{ width: '100%' }}>
                  <Stack spacing={2}>
                    {availableDisciplines.map(discipline => {
                      const isSelected = formData.permissions.disciplines.some(d => d.id === discipline.id);
                      const hasSubDisciplines = discipline.sub_disciplines && discipline.sub_disciplines.length > 0;
                      
                      return (
                        <Box
                          key={discipline.id}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            bgcolor: isSelected ? 'primary.50' : 'background.paper',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: isSelected ? 'primary.100' : 'grey.50'
                            }
                          }}
                        >
                          {/* Main Discipline */}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleDisciplineChange(discipline.id, e.target.checked)}
                                sx={{
                                  '&.Mui-checked': {
                                    color: 'primary.main'
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                color={isSelected ? 'primary.main' : 'text.primary'}
                              >
                                {discipline.name}
                              </Typography>
                            }
                            sx={{ mb: hasSubDisciplines ? 1 : 0 }}
                          />

                          {/* Sub-disciplines */}
                          {hasSubDisciplines && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                              <Divider sx={{ mb: 2, opacity: 0.5 }} />
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  display: 'block', 
                                  mb: 1.5,
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5
                                }}
                              >
                                Sub-disciplines
                              </Typography>
                              
                              <Grid container spacing={1}>
                                {discipline.sub_disciplines.map(sd => {
                                  const isSubSelected = formData.permissions.sub_disciplines.includes(sd.name);
                                  
                                  return (
                                    <Grid item xs={12} sm={6} key={sd.name}>
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          borderRadius: 1,
                                          border: '1px solid',
                                          borderColor: isSelected 
                                            ? (isSubSelected ? 'success.main' : 'grey.300')
                                            : 'grey.200',
                                          bgcolor: isSelected
                                            ? (isSubSelected ? 'success.50' : 'grey.50')
                                            : 'grey.100',
                                          transition: 'all 0.2s ease-in-out',
                                          opacity: isSelected ? 1 : 0.6,
                                          '&:hover': isSelected ? {
                                            borderColor: isSubSelected ? 'success.dark' : 'primary.main',
                                            bgcolor: isSubSelected ? 'success.100' : 'primary.50'
                                          } : {}
                                        }}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={isSubSelected}
                                              onChange={(e) => handleSubDisciplineChange(sd.name, e.target.checked)}
                                              disabled={!isSelected}
                                              size="small"
                                              sx={{
                                                '&.Mui-checked': {
                                                  color: 'success.main'
                                                },
                                                p: 0.5
                                              }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                color={
                                                  !isSelected 
                                                    ? 'text.disabled' 
                                                    : isSubSelected 
                                                      ? 'success.dark' 
                                                      : 'text.primary'
                                                }
                                              >
                                                {sd.name}
                                              </Typography>
                                              {!isSelected && (
                                                <Typography 
                                                  variant="caption" 
                                                  color="text.disabled"
                                                  sx={{ 
                                                    fontStyle: 'italic',
                                                    display: 'block'
                                                  }}
                                                >
                                                  Requires {discipline.name}
                                                </Typography>
                                              )}
                                            </Box>
                                          }
                                          sx={{ 
                                            m: 0,
                                            width: '100%',
                                            alignItems: 'center',
                                            '& .MuiFormControlLabel-label': {
                                              marginLeft: 1
                                            }
                                          }}
                                        />
                                      </Box>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                  
                  {errors.disciplines && (
                    <FormHelperText sx={{ mt: 2, fontSize: '0.875rem' }}>
                      {errors.disciplines}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Summary */}
                {formData.permissions.disciplines.length > 0 && (
                  <Alert severity="success" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      <strong>Summary:</strong> User will have access to{' '}
                      {formData.permissions.disciplines.length} discipline(s) and{' '}
                      {formData.permissions.sub_disciplines.length} sub-discipline(s).
                    </Typography>
                  </Alert>
                )}
              </Paper>
            )}

            {/* Password Management - Only for Managers */}
            {canEditRole() && currentUser?.role === 'manager' && user?.id !== currentUser?.id && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <PasswordIcon />
                  <Typography variant="h6" fontWeight={600}>Password Management</Typography>
                </Stack>

                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Password has been successfully reset for {user.full_name || user.username}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={passwordData.showPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      placeholder="Enter new password or generate one"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Stack direction="row" spacing={1}>
                              {passwordData.newPassword && (
                                <Tooltip title="Copy password">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(passwordData.newPassword)}
                                  >
                                    <CopyIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={passwordData.showPassword ? 'Hide password' : 'Show password'}>
                                <IconButton
                                  size="small"
                                  onClick={() => setPasswordData(prev => ({ 
                                    ...prev, 
                                    showPassword: !prev.showPassword 
                                  }))}
                                >
                                  {passwordData.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        startIcon={passwordData.isGenerating ? <CircularProgress size={16} /> : <GenerateIcon />}
                        onClick={generatePassword}
                        disabled={passwordData.isGenerating}
                      >
                        {passwordData.isGenerating ? 'Generating...' : 'Generate Secure Password'}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={handleResetPassword}
                        disabled={!hasPasswordChanges || loading}
                        color="warning"
                      >
                        Reset Password
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> The user will need to use this new password for their next login. 
                    Make sure to securely share this password with them.
                  </Typography>
                </Alert>
              </Paper>
            )}

            {/* Current Permissions Summary */}
            {user.permission_scope === 'network_section' && formData.permissions && formData.permissions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Current Permissions Summary
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {formData.permissions.map((permission, index) => (
                    <Chip
                      key={index}
                      label={`${permission.name} (${permission.type})`}
                      size="small"
                      color={permission.type === 'network' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={loading || !permissionsLoaded || !hasChanges || !canEditPermissions()}
          startIcon={<SaveIcon />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
